import { Link } from "react-router-dom";
import type { NavSite } from "@/mocks/navSites";
import { getCategoryById } from "@/mocks/navCategories";

interface SiteCardProps {
  site: NavSite;
  variant?: "grid" | "list" | "compact";
  rank?: number;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
}

export default function SiteCard({
  site,
  variant = "grid",
  rank,
  isFavorite = false,
  onToggleFavorite,
}: SiteCardProps) {
  const category = getCategoryById(site.categoryId);

  const favoriteButton = onToggleFavorite ? (
    <button
      type="button"
      aria-label={isFavorite ? "取消收藏" : "收藏"}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggleFavorite(site.id);
      }}
      className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
        isFavorite
          ? "text-accent-600 bg-accent-50"
          : "text-foreground-300 bg-background-100 hover:text-accent-500"
      }`}
    >
      <i className={isFavorite ? "ri-star-fill text-sm" : "ri-star-line text-sm"} />
    </button>
  ) : null;

  if (variant === "compact") {
    return (
      <Link
        to={`/detail/${site.id}`}
        className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-background-100 transition-colors cursor-pointer group"
      >
        {rank ? (
          <span
            className={`w-5 text-center text-xs font-heading font-bold ${
              rank <= 3 ? "text-accent-500" : "text-foreground-300"
            }`}
          >
            {rank}
          </span>
        ) : null}
        <span className="w-8 h-8 shrink-0 rounded-md bg-primary-50 text-primary-600 text-sm font-bold flex items-center justify-center">
          {site.letter}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-foreground-900 group-hover:text-primary-600 truncate">
            {site.name}
          </span>
          <span className="block text-xs text-foreground-400 truncate">{site.summary}</span>
        </span>
      </Link>
    );
  }

  if (variant === "list") {
    return (
      <Link
        to={`/detail/${site.id}`}
        className="group flex items-start gap-3 p-3 md:p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <span className="w-11 h-11 shrink-0 rounded-md bg-primary-50 text-primary-600 font-heading font-bold text-base flex items-center justify-center">
          {site.letter}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 truncate">
              {site.name}
            </span>
            <span className="text-[11px] text-foreground-400 truncate hidden sm:block">
              {site.host}
            </span>
          </span>
          <span className="mt-1 block text-xs text-foreground-500 line-clamp-2 leading-5">
            {site.summary}
          </span>
          <span className="mt-2 hidden md:flex flex-wrap gap-1">
            {site.tags.slice(0, 2).map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-800 text-[11px]"
              >
                {tag}
              </span>
            ))}
          </span>
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/${site.id}`}
      className="group relative flex flex-col p-4 md:p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 shrink-0 rounded-md bg-primary-50 text-primary-600 font-heading font-bold text-base flex items-center justify-center">
          {site.letter}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm md:text-base font-medium text-foreground-950 group-hover:text-primary-600 truncate">
              {site.name}
            </h3>
            {site.featured && (
              <span className="shrink-0 px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 text-[11px]">
                精选
              </span>
            )}
          </div>
          <p className="text-xs text-foreground-400 truncate mt-1">{site.host}</p>
        </div>
        {favoriteButton}
      </div>
      <p className="mt-3 text-xs md:text-sm text-foreground-600 leading-6 line-clamp-2">
        {site.summary}
      </p>
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        <span className="px-1.5 py-0.5 rounded bg-primary-50 text-primary-700 text-[11px]">
          {category?.name ?? "其他"}
        </span>
        {site.tags.slice(0, 2).map((tag) => (
          <span
            key={tag}
            className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-800 text-[11px]"
          >
            {tag}
          </span>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-background-100 flex items-center justify-between text-[11px] text-foreground-400">
        <span className="flex items-center gap-1">
          <i className="ri-eye-line" />
          {(site.visits / 10000).toFixed(1)} 万次访问
        </span>
        <span className="flex items-center gap-0.5 text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity">
          查看详情
          <i className="ri-arrow-right-line" />
        </span>
      </div>
    </Link>
  );
}